import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Search, Sparkles, Star, X } from 'lucide-react';
import { Input } from '@/shared/ui/Input';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import type { UserScreenNode } from '@/shared/types/system';
import { useTabsStore } from '../tabs/tabsStore';
import { useShellStore } from '../model/shellStore';
import { NavNode } from './NavNode';
import { NavIcon } from './NavIcon';
import { filterTree, useMenu } from './useMenu';
import { DashboardsGroup, useDashboards } from './DashboardsGroup';
import { AiReportsGroup, useAiReportsMenu } from './AiReportsGroup';
import { MenuCategoryBar } from './MenuCategoryBar';
import { NavSection } from './NavSection';
import { leafNodes, resolveCategory, visibleCategories, type MenuCategory } from './menuCategories';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { TruncatedText } from '@/shared/ui/TruncatedText';

/**
 * Sol menü: arama, bölüm şeridi (Tümü · Favoriler · Dashboard'lar · Ekranlar)
 * ve seçili bölümün içeriği.
 *
 * Bölümler veriye göre oluşur; boş olan hiç çıkmaz. Arama yazıldığında bölüm ayrımı
 * devre dışı kalır ve tüm ağaçta aranır — kullanıcı aradığı şeyin hangi bölümde
 * olduğunu bilmek zorunda değil.
 *
 * "Tümü"de üç bölüm de açılıp kapanabilir; uzun favori listesi ekran ağacını ekranın
 * dışına itmesin. İlk görünüm Blazor ile aynı: Dashboard'lar ve Favoriler KAPALI,
 * ekran bölümü açık (Blazor'da ActiveLevel1GroupId=null ve ağaç grup içinde değil).
 * Kullanıcının açıp kapattığı durum localStorage'da kalır, varsayılan yalnızca ilk
 * açılışta geçerlidir.
 *
 * Bir bölüm ÇİPTEN seçildiğinde içeriği doğrudan görünür (daraltılabilir başlık olmadan),
 * ama ekran ağacı yine kapalı başlar: bölümü seçmek "hepsini dök" demek değil.
 */
export function NavMenu() {
  const { t } = useTranslation();
  const { tree, favorites, loaded } = useMenu();
  const dashboards = useDashboards();
  const aiReports = useAiReportsMenu();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<MenuCategory>('all');
  const q = useDebouncedValue(query, 80);
  const searchRef = useRef<HTMLInputElement>(null);

  // Ctrl+K menü aramasına odaklanır (#497; daralmış menüde RailSearch paneli açar). Kutu
  // görünmüyorsa (kapalı mobil çekmece) karışmaz.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'k') return;
      const el = searchRef.current;
      if (!el || !el.offsetParent) return;
      e.preventDefault();
      el.focus();
      el.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = useMemo(() => filterTree(tree, q), [tree, q]);
  const leaves = useMemo(() => leafNodes(tree), [tree]);
  // Sayaç yaprak sayısından gelir; bölümün kendisi ağacı gösterir.
  const screenCount = leaves.length;

  const counts = useMemo(
    () => ({
      favorites: favorites.length,
      dashboards: dashboards.length,
      aiReports: aiReports.length,
      screens: screenCount,
    }),
    [favorites.length, dashboards.length, aiReports.length, screenCount],
  );
  const categories = useMemo(() => visibleCategories(counts, tree.length), [counts, tree.length]);
  const active = resolveCategory(category, categories);

  const activeTab = useTabsStore((s) => s.tabs.find((x) => x.tabId === s.activeTabId));
  const openScreen = useOpenScreen();
  const setDrawer = useShellStore((s) => s.setDrawer);

  const onOpen = (node: UserScreenNode) => {
    openScreen(node);
    setDrawer(false);
  };

  /** Düz liste bölümleri (favoriler) aynı satır biçimini paylaşır. */
  const flatList = (nodes: UserScreenNode[]) =>
    nodes.map((n) => (
      <button
        key={n.screenId}
        type="button"
        onClick={() => onOpen(n)}
        className="hover:bg-bg-hover flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm"
      >
        <NavIcon node={n} />
        <TruncatedText text={n.caption} className="min-w-0 flex-1" />
      </button>
    ));

  return (
    <nav aria-label={t('shell.menu')} className="flex h-full flex-col">
      <div className="relative p-2">
        <Search className="text-fg-subtle pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2" />
        <Input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // Esc önce metni temizler (ortak arama davranışı, #456).
            if (e.key === 'Escape' && query) {
              e.preventDefault();
              setQuery('');
            }
          }}
          title={`${t('shell.searchMenu')} (Ctrl+K)`}
          placeholder={t('shell.searchMenu')}
          className="pr-8 pl-8"
          aria-label={t('shell.searchMenu')}
        />
        {/* Temizleme düğmesi: aramayı bırakmak için metni elle silmek gerekiyordu. */}
        {query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label={t('shell.clearSearch')}
            title={t('shell.clearSearch')}
            className="text-fg-subtle hover:text-fg absolute top-1/2 right-4 -translate-y-1/2 rounded p-0.5"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Arama sırasında bölüm şeridi gizlenir: arama zaten tüm ağaçta çalışıyor. */}
      {!q ? (
        <MenuCategoryBar categories={categories} value={active} onChange={setCategory} counts={counts} />
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-4">
        {!loaded ? <div className="text-fg-muted px-2 py-2 text-sm">{t('app.loading')}</div> : null}

        {q ? (
          filtered.length ? (
            filtered.map((n) => (
              <NavNode
                key={n.screenId}
                node={n}
                depth={0}
                forceOpen
                activeScreenId={activeTab?.screenId ?? null}
                onOpen={onOpen}
              />
            ))
          ) : (
            <p className="text-fg-muted px-2 py-3 text-sm">{t('shell.noMenuResult')}</p>
          )
        ) : active === 'all' ? (
          <>
            {dashboards.length ? (
              <NavSection
                id="dashboards"
                title={t('shell.dashboards')}
                icon={<BarChart3 className="h-3 w-3" />}
                count={dashboards.length}
                defaultCollapsed
              >
                <DashboardsGroup headless />
              </NavSection>
            ) : null}
            {/* AI ile üretilip menüye kaydedilen raporlar: dashboard'lar gibi kullanıcıya özel bölüm. */}
            {aiReports.length ? (
              <NavSection
                id="aiReports"
                title={t('shell.aiReports')}
                icon={<Sparkles className="h-3 w-3" />}
                count={aiReports.length}
                defaultCollapsed
              >
                <AiReportsGroup headless />
              </NavSection>
            ) : null}
            {favorites.length ? (
              <NavSection
                id="favorites"
                title={t('shell.favorites')}
                icon={<Star className="h-3 w-3" />}
                count={favorites.length}
                defaultCollapsed
              >
                {flatList(favorites)}
              </NavSection>
            ) : null}
            {/* Sayaç şeritteki "Ekranlar" çipiyle aynı şeyi saymalı (yaprak sayısı);
                kök modül sayısı yazmak aynı ada iki farklı rakam veriyordu. */}
            <NavSection id="modules" title={t('shell.category.screens')} count={screenCount}>
              {tree.map((n) => (
                <NavNode
                  key={n.screenId}
                  node={n}
                  depth={0}
                  activeScreenId={activeTab?.screenId ?? null}
                  onOpen={onOpen}
                />
              ))}
            </NavSection>
          </>
        ) : active === 'dashboards' ? (
          <DashboardsGroup headless />
        ) : active === 'aiReports' ? (
          <AiReportsGroup headless />
        ) : active === 'favorites' ? (
          flatList(favorites)
        ) : (
          // Ekranlar: aynı modül ağacı, dashboard ve favoriler olmadan. Ağaç "Tümü"deki
          // gibi kapalı başlar — yalnızca modüller görünür, kullanıcı açarak iner. Bir ara
          // tamamen açık geliyordu; 170 ekranı tek listede dökmek aramayı zorlaştırıyordu.
          tree.map((n) => (
            <NavNode
              key={n.screenId}
              node={n}
              depth={0}
              activeScreenId={activeTab?.screenId ?? null}
              onOpen={onOpen}
            />
          ))
        )}
      </div>
    </nav>
  );
}
